/**
 * Allocation (constant-sum) questions (M4S-5777, PRD X63). Spec: docs/pdlc/crosstabs-question-types.md §4.
 *
 * The one home of what an allocation question is, how a respondent's answer reads, and which
 * tables it draws. `question-types.guard.test.ts` keeps a second copy from growing.
 *
 * **What it is.** Portal question type 5, `MULTI_WAY`, is the survey builder's "multi-way
 * apportionment": the respondent shares a fixed amount (the question's `total`, often 100 points or
 * 100%) across a list of items. The reading comes from the code that has always tabulated it, the
 * SOTG crosstab workers (`scripts/functions.crosstab.pl`): type 5 is read "only for matrix and
 * multi-way apportionment questions", from answers of the form `key_amount:key_amount`, and only
 * where the question has a `total` preference. The Studio results page draws the same type with an
 * "Average" column (`src/@portal/questions/utils/DataTransformations.ts`). The portal enum's
 * comment ("Multi-way branching question") predates both and is not what the data holds. The
 * stored answer format is read from that code, not yet from live rows: live check L4 in the spec.
 *
 * **The answer.** `answer_value` holds one `<item key>_<amount>` part per item given anything,
 * separated like any answer (`[|:,]`). The pivot keeps each part whole (it splits on the separators
 * only), so a respondent holds tokens such as `A_30` and `B_70`. `allocationAmount` reads them: the
 * amount is after the LAST underscore, so an item key may itself hold one. An item the respondent
 * gave nothing to counts as 0 for anyone who answered the question; several amounts for one item
 * (a diary person's entries, collapsed to one person) count as their average.
 *
 * **The tables.** The question's own table is the average allocation per item, with the average
 * total beneath, among the people who answered (weighted where the table is). A second table, the
 * Allocation Distribution, shows for each item the share who gave it anything and how the amounts
 * spread over five equal bands of the total (`allocationBands`). It is an extra table: readers keyed
 * by question code leave it out (`summary-tables.ts` `isExtraBlock`, `isExtraTableTitle`).
 *
 * Pure: no app types beyond the question shape, no I/O.
 */

/** The question type the Postgres mapper gives an allocation question. */
export const ALLOCATION_TYPE = 'allocation';

/** The portal's question type id for multi-way apportionment. */
export const ALLOCATION_TYPE_ID = 5;

export function isAllocationTypeId(typeId: number | string | null | undefined): boolean {
    return typeId != null && Number(typeId) === ALLOCATION_TYPE_ID;
}

/** Whether a question (a DTO, a block) is an allocation, by its type. */
export function isAllocationQuestion(
    q: { questionType?: string | null; kind?: string | null } | null | undefined,
): boolean {
    return (q?.questionType ?? q?.kind) === ALLOCATION_TYPE;
}

const toAmount = (raw: unknown): number | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
    return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * The amount an allocation shares out: the config's `total` (`question_config`), else the stored
 * `survey_question_config` row, else null. A total that is not a positive number is no total.
 */
export function allocationTotalOf(
    config: Record<string, unknown> | null | undefined,
    stored?: { total?: string | null } | null,
): number | null {
    return toAmount(config?.total) ?? toAmount(stored?.total);
}

const TOKEN_RE = /^(.+)_(-?\d+(?:\.\d+)?)$/;

/** `A_30` → `{ item: 'A', amount: 30 }`; null for a token that is not an item and an amount. */
export function parseAllocationToken(token: string): { item: string; amount: number } | null {
    const m = TOKEN_RE.exec(token.trim());
    if (!m) return null;
    const amount = Number(m[2]);
    return Number.isFinite(amount) ? { item: m[1].toUpperCase(), amount } : null;
}

/**
 * A respondent's amount for an item, from the tokens they hold for the question: the average of
 * every amount they gave it (one, except a diary person's several entries), 0 when they answered
 * the question and gave it nothing, and null when they hold no allocation at all (not asked, or no
 * answer in the allocation shape).
 */
export function allocationAmount(
    tokens: ReadonlySet<string> | readonly string[] | undefined,
    itemCodes: readonly string[],
): number | null {
    if (!tokens) return null;
    const wanted = new Set(itemCodes.map((c) => c.toUpperCase()));
    let answered = false;
    let sum = 0;
    let n = 0;
    for (const token of tokens) {
        const parsed = parseAllocationToken(token);
        if (!parsed) continue;
        answered = true;
        if (!wanted.has(parsed.item)) continue;
        sum += parsed.amount;
        n++;
    }
    if (!answered) return null;
    return n ? sum / n : 0;
}

/** Whether a respondent holds an allocation answer for the question: the table's base. */
export function hasAllocation(tokens: ReadonlySet<string> | undefined): boolean {
    if (!tokens) return false;
    for (const token of tokens) if (parseAllocationToken(token)) return true;
    return false;
}

/** One band of the Allocation Distribution: amounts above `above`, up to and including `upTo`. */
export interface AllocationBand {
    label: string;
    /** Exclusive lower bound. */
    above: number;
    /** Inclusive upper bound; Infinity on the band past the total. */
    upTo: number;
}

/** How many equal bands the total is cut into. */
export const ALLOCATION_BANDS = 5;

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/**
 * The bands an item's amounts are counted in: "0" (gave it nothing), then five equal bands of the
 * total (1–20, 21–40, 41–60, 61–80, 81–100 on a 100-point question), then "More than <total>" only
 * when someone gave more than the total. Without a stored total the bands run to the largest amount
 * anyone gave. A total under five points gets one band per point.
 */
export function allocationBands(total: number | null, largest: number): AllocationBand[] {
    const top = total ?? Math.max(largest, 0);
    const bands: AllocationBand[] = [{ label: '0', above: -Infinity, upTo: 0 }];
    if (top <= 0) return bands;
    if (top < ALLOCATION_BANDS && Number.isInteger(top)) {
        for (let v = 1; v <= top; v++) bands.push({ label: String(v), above: v - 1, upTo: v });
    } else {
        let prev = 0;
        for (let k = 1; k <= ALLOCATION_BANDS; k++) {
            const edge = k === ALLOCATION_BANDS ? top : Math.round((top * k) / ALLOCATION_BANDS);
            const low = Number.isInteger(prev) ? prev + 1 : prev;
            bands.push({ label: `${fmt(low)}–${fmt(edge)}`, above: prev, upTo: edge });
            prev = edge;
        }
    }
    if (total !== null && largest > total) {
        bands.push({ label: `More than ${fmt(total)}`, above: total, upTo: Infinity });
    }
    return bands;
}

/** The band an amount falls in, or null for no amount. */
export function bandOf(amount: number | null, bands: readonly AllocationBand[]): number | null {
    if (amount === null) return null;
    const i = bands.findIndex((b) => amount > b.above && amount <= b.upTo);
    return i < 0 ? null : i;
}

/** The table titles' endings; `isExtraTableTitle` reads the distribution's. */
export const ALLOCATION_TITLE_SUFFIX = {
    average: ' - Average Allocation',
    distribution: ' - Allocation Distribution',
} as const;

const DISTRIBUTION_TITLE_RE = /\s[-–—]+\s*Allocation Distribution\s*$/i;

/** True for a file header that names an Allocation Distribution table. */
export function isAllocationDistributionTitle(header: string): boolean {
    return DISTRIBUTION_TITLE_RE.test(header);
}

/** The label of the row under the averages: what an average respondent shared out in all. */
export const ALLOCATION_TOTAL_LABEL = 'Total allocated';

/** The label of an item's first row in the distribution: who gave it anything. */
export function allocatedAnyLabel(item: string): string {
    return `${item} (gave any)`;
}

/** The tab the Allocation Distribution gets in the one-tab-per-question export. */
export function allocationTabName(qid: string): string {
    return `${qid.slice(0, 15).trimEnd()} Allocation`;
}

/** The scale the average rows are drawn against: the total, else the largest amount given. */
export function allocationScale(total: number | null, largest: number): number {
    return total ?? (largest > 0 ? largest : 1);
}
